/**
 * Pure game engine for CRYPTOBOOM — no React, no DOM side-effects beyond
 * the canvas context passed in. Kept as plain functions so it is trivially
 * testable and the React component stays tiny.
 *
 * State shape is a single mutable object (`s`) reused across frames for
 * performance; we avoid GC churn in the RAF loop.
 */

import sfx from "./sfx";

export const CRYPTO_LABELS = ["BTC", "ETH", "USDT", "BNB", "SOL", "XRP", "DOGE", "ADA"];
export const EXPLOSION_COLORS = ["#F7931A", "#2ecc71", "#ff5252"];

export const POWERUP_DURATIONS = { magnet: 5000, slowmo: 3000 };
export const MAGNET_RADIUS = 160;
export const SLOWMO_FACTOR = 0.5;

export function initState(W, H) {
  return {
    W,
    H,
    player: { x: W * 0.25, y: H / 2, vy: 0, r: 24 },
    gravity: 0.42,
    jumpV: -7.8,
    speed: 4.2,
    scrollX: 0,
    obstacles: [],
    coins: [],
    particles: [],
    projectiles: [],
    spawnTimer: 0,
    coinTimer: 0,
    score: 0,
    distance: 0,
    blocksDestroyed: 0,
    coinsCollected: 0,
    startedAt: performance.now(),
    alive: true,
    hitFlash: 0,
    deadAt: null,
    explosionSpawned: false,
    priceHistory: [],
    candles: [],
    candleBucket: null,
    tickCounter: 0,
    startY: H / 2,
    clientSeed: `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
    // Power-ups
    shieldActive: false,
    magnetUntil: 0,
    slowmoUntil: 0,
    powerupUsed: { shield: false, magnet: false, slowmo: false },
  };
}

export function flap(s) {
  if (!s || !s.alive) return;
  s.player.vy = s.jumpV;
  sfx.play("flap");
}

export function shoot(s, x, y) {
  if (!s || !s.alive) return;
  const dx = x - s.player.x;
  const dy = y - s.player.y;
  const len = Math.hypot(dx, dy) || 1;
  s.projectiles.push({
    x: s.player.x + 10,
    y: s.player.y,
    vx: (dx / len) * 11,
    vy: (dy / len) * 11,
    life: 700,
  });
  sfx.play("shoot");
}

/**
 * Activate a power-up in the current game session.
 * Returns true on success, false if already used this match.
 * The caller is responsible for decrementing inventory via the backend
 * BEFORE calling this function.
 */
export function activatePowerup(s, kind) {
  if (!s || !s.alive) return false;
  if (s.powerupUsed[kind]) return false;
  const now = performance.now();
  if (kind === "shield") {
    s.shieldActive = true;
  } else if (kind === "magnet") {
    s.magnetUntil = now + POWERUP_DURATIONS.magnet;
  } else if (kind === "slowmo") {
    s.slowmoUntil = now + POWERUP_DURATIONS.slowmo;
  } else {
    return false;
  }
  s.powerupUsed[kind] = true;
  sfx.play("powerup");
  return true;
}

export function spawnExplosion(s, x, y, c1, c2) {
  for (let i = 0; i < 16; i++) {
    s.particles.push({
      x,
      y,
      vx: (Math.random() - 0.5) * 6,
      vy: (Math.random() - 0.5) * 6 - 1,
      life: 500,
      maxLife: 500,
      color: i % 2 === 0 ? c1 : c2,
      size: 3,
    });
  }
}

export function spawnMegaExplosion(s, x, y) {
  for (let i = 0; i < 70; i++) {
    const ang = (i / 70) * Math.PI * 2 + Math.random() * 0.3;
    const spd = 3 + Math.random() * 9;
    const col = EXPLOSION_COLORS[i % EXPLOSION_COLORS.length];
    s.particles.push({
      x,
      y,
      vx: Math.cos(ang) * spd,
      vy: Math.sin(ang) * spd,
      life: 900 + Math.random() * 300,
      maxLife: 1200,
      color: col,
      size: 3 + Math.random() * 4,
    });
  }
}

function spawnObstacle(s) {
  const gapH = Math.max(130, s.H * 0.35 - Math.min(80, s.distance / 60));
  const gapY = 40 + Math.random() * (s.H - gapH - 80);
  const buildCol = (fromY, toY) => {
    const blocks = [];
    let y = fromY;
    while (y < toY) {
      const h = 30 + Math.floor(Math.random() * 20);
      const destructible = Math.random() < 0.55;
      blocks.push({
        y,
        h: Math.min(h, toY - y),
        destructible,
        label: destructible
          ? CRYPTO_LABELS[Math.floor(Math.random() * CRYPTO_LABELS.length)]
          : null,
        alive: true,
      });
      y += h;
    }
    return blocks;
  };
  s.obstacles.push({
    x: s.W + 40,
    topBlocks: buildCol(0, gapY),
    bottomBlocks: buildCol(gapY + gapH, s.H - 20),
    scored: false,
  });
}

// Price-history/candle bookkeeping for the memecoin-chart background.
function updateChart(s) {
  const priceNow = Math.max(0, Math.min(1, 1 - s.player.y / s.H));
  s.priceHistory.push({ x: 0, price: priceNow });
  const rightEdge = s.player.x + 28;
  const leftMost = -30;
  const span = rightEdge - leftMost;
  const maxPoints = Math.min(240, Math.max(60, Math.floor(span / 2)));
  if (s.priceHistory.length > maxPoints) {
    s.priceHistory.splice(0, s.priceHistory.length - maxPoints);
  }
  const keep = s.priceHistory.length;
  for (let i = 0; i < keep; i++) {
    s.priceHistory[i].x = leftMost + (i / Math.max(1, keep - 1)) * span;
  }
  if (!s.candleBucket) {
    s.candleBucket = { o: priceNow, h: priceNow, l: priceNow, c: priceNow, n: 0 };
  }
  s.candleBucket.c = priceNow;
  s.candleBucket.h = Math.max(s.candleBucket.h, priceNow);
  s.candleBucket.l = Math.min(s.candleBucket.l, priceNow);
  s.candleBucket.n += 1;
  if (s.candleBucket.n >= 18) {
    s.candles.push({
      o: s.candleBucket.o,
      h: s.candleBucket.h,
      l: s.candleBucket.l,
      c: s.candleBucket.c,
    });
    if (s.candles.length > 80) s.candles.shift();
    s.candleBucket = { o: priceNow, h: priceNow, l: priceNow, c: priceNow, n: 0 };
  }
}

function updateParticles(s, dt) {
  for (const pt of s.particles) {
    pt.x += pt.vx;
    pt.y += pt.vy;
    pt.vy += 0.1;
    pt.life -= dt;
  }
  s.particles = s.particles.filter((p) => p.life > 0);
}

function handleCollisions(s) {
  for (const o of s.obstacles) {
    o.x -= s.speed;
    const cx = o.x + 22;
    const checkHit = (blocks) => {
      for (const b of blocks) {
        if (!b.alive) continue;
        const bx = o.x;
        const bw = 44;
        const by = b.y;
        const bh = b.h;
        const px = s.player.x;
        const py = s.player.y;
        const r = s.player.r - 2;
        const nx = Math.max(bx, Math.min(px, bx + bw));
        const ny = Math.max(by, Math.min(py, by + bh));
        const dx = px - nx;
        const dy = py - ny;
        if (dx * dx + dy * dy < r * r) {
          if (b.destructible) {
            b.alive = false;
            s.blocksDestroyed += 1;
            s.score += 10;
            spawnExplosion(s, bx + bw / 2, by + bh / 2, "#2ecc71", "#F7931A");
            sfx.play("explosion");
          } else if (s.shieldActive) {
            // Shield absorbs the hit and is consumed
            s.shieldActive = false;
            s.hitFlash = 300;
            b.alive = true; // block stays
            spawnExplosion(s, bx + bw / 2, by + bh / 2, "#00aaff", "#2ecc71");
            sfx.play("hit");
          } else {
            s.alive = false;
            s.hitFlash = 300;
            s.deadAt = performance.now();
            spawnMegaExplosion(s, s.player.x, s.player.y);
            sfx.play("gameover");
          }
        }
      }
    };
    checkHit(o.topBlocks);
    checkHit(o.bottomBlocks);
    if (!o.scored && cx + 44 < s.player.x) {
      o.scored = true;
      s.score += 5;
    }
  }
  s.obstacles = s.obstacles.filter((o) => o.x > -80);
}

function handleCoins(s) {
  const now = performance.now();
  const magnetActive = now < s.magnetUntil;
  for (const c of s.coins) {
    c.x -= s.speed;
    if (magnetActive && c.alive) {
      const dxm = s.player.x - c.x;
      const dym = s.player.y - c.y;
      const dist = Math.hypot(dxm, dym);
      if (dist < MAGNET_RADIUS && dist > 0.1) {
        const pull = 4;
        c.x += (dxm / dist) * pull;
        c.y += (dym / dist) * pull;
      }
    }
    const dx = c.x - s.player.x;
    const dy = c.y - s.player.y;
    if (c.alive && dx * dx + dy * dy < (s.player.r + 10) ** 2) {
      c.alive = false;
      s.coinsCollected += 1;
      s.score += 5;
      spawnExplosion(s, c.x, c.y, "#F7931A", "#F7931A");
      sfx.play("coin");
    }
  }
  s.coins = s.coins.filter((c) => c.alive && c.x > -20);
}

function handleProjectiles(s, dt) {
  for (const p of s.projectiles) {
    p.x += p.vx;
    p.y += p.vy;
    p.life -= dt;
    for (const o of s.obstacles) {
      const bx = o.x;
      if (p.x < bx || p.x > bx + 44) continue;
      const check = (blocks) => {
        for (const b of blocks) {
          if (!b.alive) continue;
          if (p.y >= b.y && p.y <= b.y + b.h && b.destructible) {
            b.alive = false;
            s.blocksDestroyed += 1;
            s.score += 10;
            spawnExplosion(s, bx + 22, b.y + b.h / 2, "#2ecc71", "#F7931A");
            sfx.play("explosion");
            p.life = 0;
          }
        }
      };
      check(o.topBlocks);
      check(o.bottomBlocks);
    }
  }
  s.projectiles = s.projectiles.filter(
    (p) => p.life > 0 && p.x < s.W + 20 && p.x > -20,
  );
}

export function step(s, dt) {
  updateChart(s);

  if (!s.alive) {
    updateParticles(s, dt);
    return;
  }

  const now = performance.now();
  const slowmoActive = now < s.slowmoUntil;
  const effectiveSpeed = slowmoActive ? s.speed * SLOWMO_FACTOR : s.speed;
  const speedBackup = s.speed;
  s.speed = effectiveSpeed;

  s.distance += s.speed;
  s.scrollX += s.speed;
  s.player.vy += s.gravity;
  s.player.y += s.player.vy;
  s.hitFlash = Math.max(0, s.hitFlash - dt);

  if (s.player.y - s.player.r < 0) {
    s.player.y = s.player.r;
    s.player.vy = 0;
  }
  if (s.player.y + s.player.r > s.H) {
    s.alive = false;
    s.deadAt = performance.now();
    spawnMegaExplosion(s, s.player.x, s.H - s.player.r);
    sfx.play("gameover");
  }

  s.spawnTimer += dt;
  if (s.spawnTimer > 1300) {
    spawnObstacle(s);
    s.spawnTimer = 0;
  }
  s.coinTimer += dt;
  if (s.coinTimer > 900) {
    s.coins.push({
      x: s.W + 20,
      y: 60 + Math.random() * (s.H - 120),
      alive: true,
    });
    s.coinTimer = 0;
  }

  handleCollisions(s);
  handleCoins(s);
  handleProjectiles(s, dt);
  updateParticles(s, dt);
  s.score += 0.02; // small per-frame distance contribution

  // Restore base speed so the slowmo doesn't compound over frames
  s.speed = speedBackup;
}
